<template>
    <VueFinalModal
        class="flex items-center justify-center"
        content-class="relative flex flex-col mx-4 p-4 bg-white dark:bg-surface-card rounded-lg w-full max-w-2xl"
        overlay-transition="vfm-fade"
        content-transition="vfm-fade"
        :click-to-close="true"
        :esc-to-close="true"
        @close="handleClose"
    >
        <div class="p-5" tabindex="-1">
            <button 
                aria-label="Close modal" 
                class="modal-close absolute right-4 top-4 text-black dark:text-txt-headings hover:text-gray-600 hover:dark:text-txt-body z-10 text-4xl"
                @click="handleClose"
            >
                ×
            </button>

            <div class="modal-header mb-4">
                <h2 id="modal-title" class="text-2xl font-semibold">Pridať člena</h2>
            </div>

            <div class="modal-body">
                <div class="space-y-6">
                    <CFormGroup label="Pozvať nového člena organizácie">
                        <CFormInput v-model="email" autocomplete="email" placeholder="Email" />
                    </CFormGroup>

                    <CFormGroup label="Text pozvánky">
                        <CFormTextarea v-model="note" />
                    </CFormGroup>
                </div>
            </div>

            <div class="modal-footer mt-4">
                <div class="flex justify-end space-x-3">
                    <CButton 
                        :disabled="isSubmitting"
                        variant="default" 
                        @click="handleClose"
                    >
                        Zrušiť
                    </CButton>
                    <CButton 
                        :disabled="isSubmitting" 
                        :loading="isSubmitting" 
                        variant="primary" 
                        @click="submit"
                    >
                        Odoslať pozvánku
                    </CButton>
                </div>
            </div>
        </div>
    </VueFinalModal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { VueFinalModal } from 'vue-final-modal'
import type { Org } from '#shared/types/Org'
import { OrgSendInvitationError, useOrgs } from '@/composables/orgs/useOrgs'
import { validateEmail } from '@/validators/privateProfile'
import { useToast } from '@/composables/useToast'

const { $auth } = useNuxtApp()
const { addToOrganization, inviteToOrganization, lookUpUserIdByEmail } = useOrgs()
const { showSuccess, showError } = useToast()

const props = defineProps<{
    organization: Org
}>()

const emit = defineEmits(['close'])

const email = ref('')
const note = ref(`Pridaj sa do našej organizácie ${props.organization.title} na growni.sk.`)
const isSubmitting = ref(false)

const submit = async () => {
    isSubmitting.value = true

    let result

    try {
        const emailValue = email.value.trim().toLowerCase()

        const { isValid } = await validateEmail(emailValue)

        if (!isValid) {
            showError('Neplatný email.')
            return
        }

        const userId = await lookUpUserIdByEmail(emailValue)

        if (userId) {
            result = await addToOrganization({
                toUserId: userId,
                orgId: props.organization.id!,
                note: note.value
            })

            if (result) {
                showSuccess('Užívateľ bol pridaný do organizácie.')
            }
        } else {
            result = await inviteToOrganization({
                email: emailValue,
                orgId: props.organization.id!,
                invitedById: $auth.currentUser!.uid,
                note: note.value
            })

            if (result) {
                showSuccess(
                    `${emailValue} bol pozvaný do organizácie. Po zaregistrovaní sa na growni.sk sa automaticky stane jej členom.`
                )
            }
        }
    } catch (e) {
        // Keep the modal open and say what happened — failures used to close it silently,
        // so people could not tell that nobody had been invited.
        console.error('Org invitation failed:', e)
        showError(
            e instanceof OrgSendInvitationError
                ? e.message
                : 'Pozvánku sa nepodarilo odoslať. Skús to prosím znova.'
        )
        return
    } finally {
        isSubmitting.value = false
    }

    handleClose(result)
}

const handleClose = (value?: any) => {
    emit('close', value)
}

</script>
